import { sanitizeError } from '@heidi/runtime/logger';
import {
  appendCheckpoint,
  finishTracking,
  startTracking,
  TrackingKey,
  type GenerateTrigger as NoteGenerateTrigger,
  type TrackingHandle,
} from '@heidi/runtime/performance';
import { type EHRProvider, type GenerationMethod } from '@heidi/schema/ml-scribe';

import {
  createExperimentProps,
  GET_SESSION_BY_ID,
  NoteFailedClientErrorCode,
  type QuerySessionResponse,
} from '../../../contracts';
import { getSessionDataConfig } from '../../config';
import { getConsultNoteSettingsFromQueryCache } from '../consultNoteStreamHelpers';
import { type RecoveryStreamResult } from '../generation-abort-reasons';
import { type GenerationCancellationReason } from '../generation-coordinator';
import { noteGenerationLogger } from '../noteGenerationLogger';
import { getNoteGenerationEngineConfig } from './config';
import { parseNoteFailedErrorCode } from './parseNoteFailedErrorCode';

export interface NoteGenerationTimings {
  startTime: number;
  /**
   * Handle for the Datadog RUM performance-tracking run (perf.scribe.note_generation).
   * Instanced so two *distinct* concurrent generations (e.g. regenerate-while-streaming,
   * or different sessions) don't clobber each other's timing. Within a single
   * side-by-side generation all candidates deliberately share this one handle — the run
   * measures the whole generation (time until all candidates are ready), not individual
   * candidates, and the SBS path emits no per-phase checkpoints. Threaded to every
   * checkpoint/finish call below.
   */
  perfHandle?: TrackingHandle;
  waitingUploadStartTime?: number;
  waitingUploadEndTime?: number;
  waitingStreamStartTime?: number;
  waitingStreamEndTime?: number;
  streamingStartTime?: number;
  streamingEndTime?: number;
  endTime?: number;
}

type NoteGenerationPhase =
  | 'waitingUploadStart'
  | 'waitingUploadEnd'
  | 'waitingStreamStart'
  | 'waitingStreamEnd'
  | 'streamingStart'
  | 'streamingEnd';

const CHECKPOINT_NAME_BY_PHASE: Record<NoteGenerationPhase, string> = {
  waitingUploadStart: 'waiting_upload_start',
  waitingUploadEnd: 'waiting_upload_end',
  waitingStreamStart: 'waiting_stream_start',
  waitingStreamEnd: 'waiting_stream_end',
  streamingStart: 'streaming_start',
  streamingEnd: 'streaming_end',
};

function toErrorReason(error: unknown): string | undefined {
  const code = parseNoteFailedErrorCode(error);
  return code === 'unknown' ? undefined : code;
}

/** A failure already raised as a `NoteGenerate*` name passes through unchanged. */
function toNoteGenerateFailure(error: unknown): Error {
  if (getNoteGenerationEngineConfig().telemetry.isNoteGenerateError(error)) return error;
  const code = parseNoteFailedErrorCode(error);
  if (
    code === NoteFailedClientErrorCode.TypeError &&
    !getNoteGenerationEngineConfig().telemetry.isFetchFailure(error)
  ) {
    return getNoteGenerationEngineConfig().telemetry.classifyNoteGenerateFailureCode(
      NoteFailedClientErrorCode.Unknown
    );
  }
  return getNoteGenerationEngineConfig().telemetry.classifyNoteGenerateFailureCode(code);
}

/**
 * Same pause-aware value the session header shows; absent (not 0) for
 * sessions with no recording, e.g. uploaded audio or dictation.
 */
function recordingDurationMetadata(
  sessionId: string
): { recordingDurationS: number } | Record<string, never> {
  const duration = getSessionDataConfig()
    .getQueryClient()
    .getQueryData<QuerySessionResponse>([GET_SESSION_BY_ID, { sessionId }])?.duration;
  const seconds = typeof duration === 'number' ? Math.round(duration) : 0;
  return seconds > 0 ? { recordingDurationS: seconds } : {};
}

export interface NoteGenerationEventContext {
  sessionId: string;
  documentId?: string;
  isRecordingV2Enabled?: boolean;
  isTabSharing?: boolean;
  is_pinned_template: boolean;
  is_starter_pack_template: boolean;
  generation_method?: GenerationMethod | null;
  is_haiku_integration?: boolean;
  trigger?: NoteGenerateTrigger;
}

/**
 * Tracks the start of note generation process
 * Used by both regular _streamResponse and SBS flows
 */
export function trackNoteGenerationStart(
  context: NoteGenerationEventContext
): NoteGenerationTimings {
  const startTime = performance.now();
  const startTimestamp = startTime + performance.timeOrigin;

  getNoteGenerationEngineConfig().analytics.noteGenerationStart({
    sessionId: context.sessionId,
    documentId: context.documentId,
    startTime: startTimestamp,
    isRecordingV2Enabled:
      context.isRecordingV2Enabled ??
      getNoteGenerationEngineConfig().isRecordingV2Enabled(),
    isTabSharing: context.isTabSharing ?? false,
    is_pinned_template: context.is_pinned_template,
    is_starter_pack_template: context.is_starter_pack_template,
    generation_method: context.generation_method,
  });

  // Datadog RUM duration vital (SLO-queryable). Instanced so concurrent
  // generations don't overwrite one another's timing.
  const perfHandle = startTracking({
    key: TrackingKey.SCRIBE_NOTE_GENERATION,
    instanced: true,
    metadata: {
      session_id: context.sessionId,
      generation_method: context.generation_method ?? undefined,
      is_recording_v2_enabled:
        context.isRecordingV2Enabled ??
        getNoteGenerationEngineConfig().isRecordingV2Enabled(),
      is_tab_sharing: context.isTabSharing ?? false,
      is_pinned_template: context.is_pinned_template,
      is_starter_pack_template: context.is_starter_pack_template,
      is_haiku_integration: context.is_haiku_integration ?? false,
      ...(context.trigger && { trigger: context.trigger }),
    },
  });

  return {
    startTime,
    perfHandle,
  };
}

/**
 * Tracks the completion of note generation process
 * Used by both regular _streamResponse and SBS flows
 */
export function trackNoteGenerationFinished(
  context: NoteGenerationEventContext,
  timings: NoteGenerationTimings,
  isSuccess: boolean,
  error?: unknown
) {
  const endTime = performance.now();
  const totalDuration = endTime - timings.startTime;

  const durationBeforeWaitingUpload = timings.waitingUploadStartTime
    ? timings.waitingUploadStartTime - timings.startTime
    : 0;

  const durationWaitingToUpload =
    timings.waitingUploadStartTime && timings.waitingUploadEndTime
      ? timings.waitingUploadEndTime - timings.waitingUploadStartTime
      : 0;

  const durationWaitingToStream =
    timings.waitingStreamStartTime && timings.waitingStreamEndTime
      ? timings.waitingStreamEndTime - timings.waitingStreamStartTime
      : 0;

  const durationStreaming =
    timings.streamingStartTime && timings.streamingEndTime
      ? timings.streamingEndTime - timings.streamingStartTime
      : 0;

  // Track finished event
  getNoteGenerationEngineConfig().analytics.noteGenerationFinished({
    sessionId: context.sessionId,
    documentId: context.documentId,
    isSuccess,
    totalDuration,
    durationBeforeWaitingUpload,
    durationWaitingToUpload,
    durationWaitingToStream,
    durationStreaming,
    isRecordingV2Enabled:
      context.isRecordingV2Enabled ??
      getNoteGenerationEngineConfig().isRecordingV2Enabled(),
    isTabSharing: context.isTabSharing ?? false,
  });

  // Track end event (for legacy compatibility)
  getNoteGenerationEngineConfig().analytics.noteGenerationEnd({
    sessionId: context.sessionId,
    documentId: context.documentId,
    endTime: endTime + performance.timeOrigin,
    is_pinned_template: context.is_pinned_template,
    is_starter_pack_template: context.is_starter_pack_template,
  });

  if (!isSuccess) {
    getNoteGenerationEngineConfig().analytics.noteGenerationError({
      sessionId: context.sessionId,
      documentId: context.documentId,
      error_code: parseNoteFailedErrorCode(error),
      is_pinned_template: context.is_pinned_template,
      is_starter_pack_template: context.is_starter_pack_template,
      generation_method: context.generation_method,
    });
  }

  // Close out the Datadog RUM duration vital. A failed generation reports with
  // result: 'error' (an SLO breach), not a cancellation, plus a PHI-safe
  // error_reason (known code or error class) so breaches are diagnosable.
  if (timings.perfHandle) {
    const errorReason = isSuccess ? undefined : toErrorReason(error);
    const target = {
      handle: timings.perfHandle,
      metadata: {
        result: isSuccess ? 'success' : 'error',
        ...(errorReason && { error_reason: errorReason }),
        ...recordingDurationMetadata(context.sessionId),
      },
    };
    if (isSuccess) {
      finishTracking(target);
    } else {
      const failure = toNoteGenerateFailure(error);
      finishTracking({
        ...target,
        outcome: 'failed',
        error: failure,
        metadata: {
          ...target.metadata,
          ...getNoteGenerationEngineConfig().telemetry.interruptionMetadata(failure),
        },
      });
    }
  }
}

/**
 * Closes the Datadog RUM handle for a non-success outcome that bypasses
 * {@link trackNoteGenerationFinished} — a user/superseded abort, or a pre-stream
 * throw caught by the stream's outer try/catch. Closes the handle ONLY; it does
 * not re-dispatch the Segment/analytics events (those are not idempotent).
 *
 * Safe to call unconditionally and idempotent on an already-closed handle, so
 * whichever outcome fires first wins — an abort recorded here is not later
 * overwritten by the `error` that the aborted stream's rejection produces. Pass
 * the caught error on the `'error'` path to record a PHI-safe `error_reason`.
 *
 * `'aborted'` (Stop, supersede, cleanup) is excluded from the SLO and a
 * `cancelled` attempt; `'error'` is a breach. Without this, a bypassed handle lingers until
 * the page-exit snapshot reports it as `abandoned`.
 */
export function finalizeNoteGenerationPerf(
  sessionId: string,
  timings: NoteGenerationTimings,
  result: 'aborted' | 'error',
  error?: unknown,
  cancelReason?: GenerationCancellationReason
): void {
  if (!timings.perfHandle) return;
  const errorReason = result === 'error' ? toErrorReason(error) : undefined;
  const failure =
    result === 'aborted'
      ? getNoteGenerationEngineConfig().telemetry.createNoteGenerateCancelledError()
      : toNoteGenerateFailure(error);
  finishTracking({
    handle: timings.perfHandle,
    outcome: 'failed',
    error: failure,
    metadata: {
      result,
      ...(errorReason && { error_reason: errorReason }),
      ...(result === 'aborted' && cancelReason && { cancel_reason: cancelReason }),
      ...getNoteGenerationEngineConfig().telemetry.interruptionMetadata(failure),
      ...recordingDurationMetadata(sessionId),
    },
  });
}

/**
 * Closes the attempt of a stuck stream that recovery tore down, with what
 * recovery found. `result` stays `'aborted'` so the legacy SLO is unchanged.
 */
export function finalizeRecoveredNoteGenerationPerf(
  sessionId: string,
  timings: NoteGenerationTimings,
  { outcome, failureReason }: RecoveryStreamResult
): void {
  if (!timings.perfHandle) return;
  const metadata = {
    result: 'aborted',
    recovery_outcome: outcome,
    ...recordingDurationMetadata(sessionId),
  };
  finishTracking(
    outcome === 'completed'
      ? { handle: timings.perfHandle, metadata }
      : {
          handle: timings.perfHandle,
          outcome: 'failed',
          error:
            getNoteGenerationEngineConfig().telemetry.createRecoveredStreamFailureError(
              outcome,
              failureReason
            ),
          metadata,
        }
  );
}

/** Which classification decided the conversion. The paths are mutually
 * exclusive: `settled_after_setup` requires the setup snapshot's atom to be
 * false, and the Hyperspace arms force the snapshot's atom true; the setup
 * snapshot covers the atom-true cohorts (first generations and
 * `useJsonTemplateDefault`-armed surfaces); `hyperspace_regen_late` is the
 * close-time re-check for a regeneration that fired before routing settled. */
export type SectionalClassificationPath =
  | 'setup'
  | 'settled_after_setup'
  | 'hyperspace_regen'
  | 'hyperspace_regen_late';

/** How many failed or empty Hyperspace restore conversions may re-arm a
 * retry. Beyond this the note stays editable markdown: no further toast,
 * no further conversion, so a persistently failing conversion cannot toast
 * on every generation. Shared with the stream handler, which owns the
 * counter; defined here so the `restore_exhausted` vital tag and the cap
 * cannot drift apart. */
export const MAX_SECTIONAL_RESTORE_ATTEMPTS = 2;

export interface SectionalConversionContext {
  sessionId: string;
  isHaikuIntegration: boolean;
  classificationPath: SectionalClassificationPath;
  /** True when the restore marker (not the note-in-use) drove the arm: the
   * conversion is retrying a failed or empty one. */
  isRestoreAttempt?: boolean;
  /** Retry number of a restore attempt (0 on the first retry), so Logs can
   * separate "a restore retry succeeded" from "a first-attempt conversion
   * succeeded", and the vital can tag the capped, no-more-retries case. */
  restoreAttemptCount?: number;
  ehrProvider?: EHRProvider;
}

export interface SectionalConversionRun {
  perfHandle: TrackingHandle;
  startTime: number;
  context: SectionalConversionContext;
}

/**
 * Opens the Datadog RUM duration vital (perf.scribe.sectional_conversion) for
 * the post-generation note→sectional conversion. The conversion runs after
 * {@link trackNoteGenerationFinished} has already closed the note-generation
 * handle as success, so it needs its own key — without one a conversion
 * outage reads as healthy note generation. Instanced for the same reason the
 * parent is: concurrent generations must not clobber each other's timing.
 */
export function trackSectionalConversionStart(
  context: SectionalConversionContext
): SectionalConversionRun {
  const perfHandle = startTracking({
    key: TrackingKey.SCRIBE_SECTIONAL_CONVERSION,
    instanced: true,
    metadata: {
      session_id: context.sessionId,
      is_haiku_integration: context.isHaikuIntegration,
      classification_path: context.classificationPath,
      ...(context.isRestoreAttempt && { restore_attempt: true }),
      ...(context.isRestoreAttempt && {
        restore_attempt_count: context.restoreAttemptCount,
      }),
      ...(context.ehrProvider && { ehr_provider: context.ehrProvider }),
    },
  });
  return { perfHandle, startTime: performance.now(), context };
}

function sectionalConversionLogFields(run: SectionalConversionRun) {
  return {
    session_id: run.context.sessionId,
    duration_ms: Math.round(performance.now() - run.startTime),
    classification_path: run.context.classificationPath,
    is_haiku_integration: run.context.isHaikuIntegration,
    ...(run.context.isRestoreAttempt && { restore_attempt: true }),
    ...(run.context.isRestoreAttempt && {
      restore_attempt_count: run.context.restoreAttemptCount,
    }),
    ...(run.context.ehrProvider && { ehr_provider: run.context.ehrProvider }),
  };
}

/**
 * Closes the vital for a 2xx response. `sectionCount` is the number of keys in
 * the returned `clinical_note` (a count, never content). Zero sections means
 * the backend left the note as markdown — indistinguishable from success
 * without this, so it is recorded as `result: 'error'` with
 * `error_reason: 'empty_note'` on the vital and is queryable as
 * `section_count: 0` on the log.
 */
export function trackSectionalConversionDone(
  run: SectionalConversionRun,
  sectionCount: number
): void {
  // A restore retry that had already exhausted the cap is the one case an
  // `empty_note` vital cannot tell apart from an ordinary empty conversion,
  // yet it is the user-visible endpoint of the bounded-retry loop.
  const isRestoreExhausted =
    sectionCount === 0 &&
    Boolean(run.context.isRestoreAttempt) &&
    (run.context.restoreAttemptCount ?? 0) >= MAX_SECTIONAL_RESTORE_ATTEMPTS;
  finishTracking({
    handle: run.perfHandle,
    metadata:
      sectionCount > 0
        ? { result: 'success' }
        : isRestoreExhausted
          ? { result: 'error', error_reason: 'restore_exhausted' }
          : { result: 'error', error_reason: 'empty_note' },
  });
  noteGenerationLogger.info('[SECTIONAL_CONVERT] done', {
    ...sectionalConversionLogFields(run),
    section_count: sectionCount,
  });
}

/**
 * Closes the vital for a rejected conversion request with
 * `error_reason: 'request_failed'`, so a monitor on the vital can separate an
 * unreachable backend from an unconverted note. Only the PHI-safe
 * `{ status, code }` projection of the error is logged: the raw axios error
 * carries the response body, and a plain `Error` serialises to `{}` anyway.
 */
export function trackSectionalConversionFailed(
  run: SectionalConversionRun,
  error: unknown
): void {
  finishTracking({
    handle: run.perfHandle,
    metadata: { result: 'error', error_reason: 'request_failed' },
  });
  noteGenerationLogger.error('[SECTIONAL_CONVERT] failed', {
    ...sectionalConversionLogFields(run),
    error: sanitizeError(error),
  });
}

/**
 * Emits `note_created` for the single-candidate stream flow. The side-by-side
 * flow emits its own from `handleStreamCloseForCandidate`; without this, the
 * default one-candidate path produces no `note_created` at all.
 */
export function trackNoteCreatedFromSingleStream({
  sessionId,
  noteId,
  noteContent,
  rawExperimentName,
  timings,
  isRegeneration = false,
}: {
  sessionId: string;
  noteId: string;
  noteContent: string;
  rawExperimentName: string;
  timings: NoteGenerationTimings;
  /** Without this a regeneration is indistinguishable from the first note. */
  isRegeneration?: boolean;
}): void {
  // Parity with the side-by-side emit: an empty close produces no note, so no
  // event — the content fallback would otherwise resolve to the previous note.
  if (!noteContent.trim()) return;

  const templateId = getConsultNoteSettingsFromQueryCache(sessionId).template_id ?? '';

  const timeToFirstToken =
    timings.streamingStartTime !== undefined
      ? timings.streamingStartTime - timings.startTime
      : undefined;
  const timeToLastToken =
    timings.streamingEndTime !== undefined
      ? timings.streamingEndTime - timings.startTime
      : undefined;

  getNoteGenerationEngineConfig().analytics.trackNoteCreated({
    ...createExperimentProps(rawExperimentName),
    note_id: noteId,
    session_id: sessionId,
    type: 'default',
    template_id: templateId,
    time_to_first_token: timeToFirstToken,
    time_to_last_token: timeToLastToken,
    section_count: getNoteGenerationEngineConfig().countMarkdownSections(noteContent),
    is_sectional: getNoteGenerationEngineConfig().isSectionalTemplate(templateId),
    is_regeneration: isRegeneration,
    // Device-file→note tracing for Chronicle-origin sessions;
    // resolves to undefined (absent) for mic/upload sessions.
    device_file_name:
      getNoteGenerationEngineConfig().getChronicleDeviceFileNameForSession(sessionId),
  });
}

/**
 * Updates timing information during the generation process
 */
export function updateNoteGenerationTimings(
  timings: NoteGenerationTimings,
  phase: NoteGenerationPhase,
  sessionId?: string
): NoteGenerationTimings {
  const now = performance.now();
  const durationSinceStartMs = Math.round(now - timings.startTime);

  if (timings.perfHandle) {
    appendCheckpoint({
      handle: timings.perfHandle,
      checkpointName: CHECKPOINT_NAME_BY_PHASE[phase],
    });
  }

  let updated: NoteGenerationTimings;
  switch (phase) {
    case 'waitingUploadStart':
      updated = { ...timings, waitingUploadStartTime: now };
      break;
    case 'waitingUploadEnd':
      updated = { ...timings, waitingUploadEndTime: now };
      break;
    case 'waitingStreamStart':
      updated = { ...timings, waitingStreamStartTime: now };
      break;
    case 'waitingStreamEnd':
      updated = { ...timings, waitingStreamEndTime: now };
      break;
    case 'streamingStart':
      updated = { ...timings, streamingStartTime: now };
      break;
    case 'streamingEnd':
      updated = { ...timings, streamingEndTime: now };
      break;
    default:
      updated = timings;
  }

  noteGenerationLogger.info('[NoteGenerationTimings] phase update', {
    sessionId,
    phase,
    durationSinceStartMs,
    timings: updated,
  });

  return updated;
}
